import { Tag } from "@/components/ui-kit";
import { ProBadge } from "@/components/dashboard-bits";
import { fileUrl } from "@/api";
import { availMeta, videoEmbedUrl } from "@/components/profile-helpers";
import { MapPin, Instagram, Globe, ExternalLink, Youtube, Music2, ArrowUpRight, Sparkles } from "lucide-react";

function MetaBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[#968F85]">{label}</div>
      <div className="mt-1.5 text-[15px] font-medium text-[#292827]">{children}</div>
    </div>
  );
}

function parseSocial(platform, value) {
  const t = (value || "").trim();
  if (!t) return null;
  let handle, href;
  if (/^https?:\/\//i.test(t)) {
    href = t;
    const seg = t.replace(/\/+$/, "").split("/").pop() || t;
    handle = seg.replace(/^@+/, "");
  } else {
    handle = t.replace(/^@+/, "");
    const bases = {
      instagram: `https://instagram.com/${handle}`,
      tiktok: `https://www.tiktok.com/@${handle}`,
      youtube: `https://www.youtube.com/@${handle}`,
    };
    href = bases[platform];
  }
  if (!handle) return null;
  return { href, handle };
}

// Shared, page-level (non-modal) creator profile view.
export function CreatorProfileView({ creator }) {
  const p = creator.profile || {};
  const photo = p.profile_photo || creator.picture;
  const initials = (creator.name || "?").slice(0, 2).toUpperCase();
  const pfImages = (p.portfolio_images || []).map(fileUrl);
  const demoThumbs = (!pfImages.length && Array.isArray(p.thumbnails)) ? p.thumbnails : [];
  const gallery = pfImages.length ? pfImages : demoThumbs;
  const pfLink = p.portfolio_url || p.portfolio?.[0]?.link;
  const embed = videoEmbedUrl(p.portfolio_video);
  const avail = p.availability ? availMeta(p.availability) : null;

  const socials = [
    { platform: "instagram", label: "Instagram", icon: Instagram, data: parseSocial("instagram", p.instagram) },
    { platform: "tiktok", label: "TikTok", icon: Music2, data: parseSocial("tiktok", p.tiktok) },
    { platform: "youtube", label: "YouTube", icon: Youtube, data: parseSocial("youtube", p.youtube) },
  ].filter((s) => s.data);

  const hasSkills = p.content_types?.length > 0;
  const hasCategories = p.categories?.length > 0;
  const hasMeta = p.price_range || p.collaboration || p.languages?.length > 0;
  const hasExplore = pfLink || gallery.length > 0 || embed || p.portfolio_video || socials.length > 0;

  return (
    <div data-testid="creator-profile-view" className="space-y-8">
      {/* Tekijän tiedot */}
      <section>
        <div className="text-xs uppercase tracking-wide text-[#968F85] mb-5">Tekijän tiedot</div>
        <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
          <div className="w-40 sm:w-48 shrink-0 aspect-[4/5] overflow-hidden rounded-2xl border border-[#E6E1D9] bg-[#E9EAEC] flex items-center justify-center font-medium text-4xl text-[#B7B0A5]" data-testid="creator-profile-photo">
            {photo ? <img src={fileUrl(photo)} alt="" className="h-full w-full object-cover" /> : initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-display text-[#292827]" data-testid="creator-profile-name">{creator.name}</h2>
              {p.is_pro && <ProBadge />}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-[15px] text-[#6E675C]">
              {p.location && <span className="flex items-center gap-1.5"><MapPin size={15} className="text-[#968F85]" /> {p.location}</span>}
              {avail && <span className={`flex items-center gap-2 ${avail.muted ? "text-[#968F85]" : ""}`}><span className={`h-2 w-2 rounded-full ${avail.dot}`} /> {avail.label}</span>}
              {p.remote === true && <span>Etäyhteistyöt onnistuvat</span>}
            </div>
            {p.bio && <p className="mt-5 text-[15px] text-[#3A3733] leading-relaxed" data-testid="creator-profile-bio">{p.bio}</p>}
          </div>
        </div>
      </section>

      {/* Sisältö & osaaminen */}
      {(hasSkills || hasCategories || hasMeta) && (
        <section className="pt-8 border-t border-[#E6E1D9]" data-testid="creator-profile-skills">
          <div className="text-xs uppercase tracking-wide text-[#968F85] mb-5">Sisältö &amp; osaaminen</div>
          {hasSkills && (
            <div data-testid="creator-profile-services">
              <div className="text-[13px] font-medium text-[#968F85] mb-2">Osaaminen</div>
              <div className="flex flex-wrap gap-2">{p.content_types.map((s) => <Tag key={s}>{s}</Tag>)}</div>
            </div>
          )}
          {hasCategories && (
            <div className={hasSkills ? "mt-6" : ""}>
              <div className="text-[13px] font-medium text-[#968F85] mb-2">Kategoriat</div>
              <div className="flex flex-wrap gap-2">{p.categories.map((s) => <Tag key={s}>{s}</Tag>)}</div>
            </div>
          )}
          {hasMeta && (
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {p.price_range && <MetaBlock label="Hinta"><span className="text-[#6E675C] font-normal">{p.price_range}</span></MetaBlock>}
              {p.collaboration && <MetaBlock label="Yhteistyöehdot">{p.collaboration}</MetaBlock>}
              {p.languages?.length > 0 && <MetaBlock label="Kielet"><span className="flex items-center gap-1.5"><Globe size={15} /> {p.languages.join(", ")}</span></MetaBlock>}
            </div>
          )}
        </section>
      )}

      {/* Tutustu tekijän sisältöön */}
      {hasExplore && (
        <section className="rounded-2xl border border-[#DCE6F5] bg-[#F5F9FE] p-6 sm:p-7" data-testid="creator-explore-content">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tightish text-[#292827]">
            <Sparkles size={18} strokeWidth={1.75} className="text-[#5B7FB0]" /> Tutustu tekijän sisältöön
          </h3>
          <p className="mt-1.5 text-[14px] text-[#6E675C] leading-relaxed">Katso millaista sisältöä tekijä luo ja tutustu hänen tyyliinsä.</p>

          {/* Portfolio */}
          {(pfLink || gallery.length > 0 || embed || p.portfolio_video) && (
            <div className="mt-5" data-testid="creator-profile-portfolio">
              <div className="text-[13px] font-medium text-[#968F85] mb-3">Portfolio</div>
              {pfLink && (
                <a href={pfLink} target="_blank" rel="noopener noreferrer" data-testid="open-portfolio"
                  className="inline-flex items-center gap-2 rounded-full bg-[#292827] px-6 py-3 text-sm font-medium text-white hover:bg-[#1c1b1a] transition-colors">
                  Katso portfolio <ArrowUpRight size={16} />
                </a>
              )}
              {gallery.length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="portfolio-gallery">
                  {gallery.slice(0, 3).map((src, i) => (
                    <div key={i} className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#E9EAEC]">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
              {embed && (
                <div className="mt-4 aspect-video rounded-2xl overflow-hidden bg-black" data-testid="portfolio-video-embed">
                  <iframe src={embed} title="Portfolio-video" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              {!embed && p.portfolio_video && (
                <a href={p.portfolio_video} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-[#292827] underline">Katso video <ExternalLink size={14} /></a>
              )}
            </div>
          )}

          {/* Somekanavat (allekkain) */}
          {socials.length > 0 && (
            <div className="mt-6" data-testid="creator-profile-socials">
              <div className="text-[13px] font-medium text-[#968F85] mb-3">Somekanavat</div>
              <div className="flex flex-col gap-3">
                {socials.map(({ platform, label, icon: Icon, data }) => (
                  <a key={platform} href={data.href} target="_blank" rel="noopener noreferrer" data-testid={`social-${platform}`}
                    className="group flex items-center gap-3 rounded-xl border border-[#DCE6F5] bg-white px-4 py-3 text-[#292827] hover:border-[#292827] transition-colors">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F9FE] text-[#292827] shrink-0"><Icon size={18} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-medium">{label}</span>
                      <span className="block text-[13px] text-[#968F85] truncate">@{data.handle}</span>
                    </span>
                    <ArrowUpRight size={16} className="text-[#968F85] group-hover:text-[#292827] transition-colors shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
